"use client";

import { ReactNode } from "react";
import { clsx } from "clsx";
import { Plus, MoreHorizontal } from "lucide-react";
import { StickyCard } from "../StickyCard";
import { StickyColor } from "../StickyNote";
import { IconButton } from "../IconButton";
import { Button } from "../Button";

// ============================================
// Type Definitions
// ============================================

interface BoardColumnItem {
  id: string;
  title: string;
  subtitle?: string;
  badges?: Array<{ label: string; color?: string }>;
  [key: string]: unknown;
}

interface BoardColumnProps<T extends BoardColumnItem> {
  /** Column unique identifier */
  id: string;
  /** Column header title */
  title: string;
  /** Item count badge */
  count?: number;
  /** Column color theme */
  color?: StickyColor;
  /** Items in this column */
  items: T[];
  /** Column width */
  width?: string;
  /** Enable drag and drop */
  draggable?: boolean;
  /** Show column menu */
  showMenu?: boolean;
  /** Column menu click handler */
  onMenuClick?: () => void;
  /** Add item button click handler */
  onAddItem?: () => void;
  /** Item click handler */
  onItemClick?: (item: T) => void;
  /** Item menu click handler */
  onItemMenuClick?: (item: T) => void;
  /** Custom item renderer */
  renderItem?: (item: T, index: number) => ReactNode;
  /** Empty state content */
  emptyState?: ReactNode;
  /** Additional header content */
  headerExtra?: ReactNode;
  /** Animation delay index for staggered entrance */
  animationIndex?: number;
  /** Custom class name */
  className?: string;
}

// ============================================
// Color mapping to StickyColor
// ============================================

const colorMapping: Record<string, StickyColor> = {
  default: "yellow",
  gray: "gray",
  brown: "orange",
  orange: "orange",
  yellow: "yellow",
  green: "green",
  blue: "blue",
  purple: "purple",
  pink: "pink",
  red: "red",
};

function mapToStickyColor(color: string): StickyColor {
  return colorMapping[color] || "yellow";
}

// ============================================
// Sub-components
// ============================================

function ColumnHeader({
  title,
  count,
  color,
  showMenu,
  onMenuClick,
  headerExtra,
}: {
  title: string;
  count?: number;
  color: StickyColor;
  showMenu?: boolean;
  onMenuClick?: () => void;
  headerExtra?: ReactNode;
}) {
  // Get the CSS variable for the color indicator
  const colorVar = `var(--sticky-${color})`;

  return (
    <div className="flex items-center gap-2 mb-4 px-1">
      {/* Color indicator */}
      <div
        className="w-3 h-3 rounded-full border-2 border-white shadow-sm flex-shrink-0"
        style={{ backgroundColor: colorVar }}
      />

      {/* Title */}
      <h2 className="text-sm font-semibold text-[var(--color-text-primary)] truncate keep-all flex-1">
        {title}
      </h2>

      {/* Count badge */}
      {count !== undefined && (
        <span className="text-xs text-[var(--color-text-muted)] tabular-nums">({count})</span>
      )}

      {/* Extra content */}
      {headerExtra}

      {/* Menu button */}
      {showMenu && onMenuClick && (
        <IconButton
          icon={<MoreHorizontal className="w-4 h-4" />}
          label="Column options"
          variant="ghost"
          size="sm"
          className="opacity-0 group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onMenuClick();
          }}
        />
      )}
    </div>
  );
}

function AddItemButton({ onClick }: { onClick?: () => void }) {
  return (
    <Button
      variant="secondary"
      size="md"
      fullWidth
      leftIcon={<Plus className="w-4 h-4" />}
      onClick={onClick}
      className="mt-2 border-dashed"
    >
      New item
    </Button>
  );
}

function EmptyColumnState() {
  return (
    <div
      className={clsx(
        "flex items-center justify-center",
        "h-32 mb-2",
        "border-2 border-dashed border-[var(--color-border)] rounded-lg",
        "bg-[var(--color-surface)]/30"
      )}
    >
      <p className="text-sm text-[var(--color-text-muted)]">No items</p>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function BoardColumn<T extends BoardColumnItem>({
  id,
  title,
  count,
  color = "yellow",
  items,
  width = "300px",
  draggable = false,
  showMenu = false,
  onMenuClick,
  onAddItem,
  onItemClick,
  onItemMenuClick,
  renderItem,
  emptyState,
  headerExtra,
  animationIndex = 0,
  className,
}: BoardColumnProps<T>) {
  const stickyColor: StickyColor = typeof color === "string" ? mapToStickyColor(color) : color;

  // Alternate rotations for visual interest
  const getItemRotation = (index: number): string => {
    const rotations = ["-1deg", "0.5deg", "-0.5deg", "1deg", "0deg"];
    return rotations[index % rotations.length];
  };

  return (
    <div
      id={id}
      draggable={draggable}
      className={clsx("group flex-shrink-0", className)}
      style={{
        width,
        animation: "fade-in-up 0.5s var(--ease-bounce) forwards",
        animationDelay: `${animationIndex * 0.08}s`,
        opacity: 0,
      }}
    >
      {/* Column Header */}
      <ColumnHeader
        title={title}
        count={count ?? items.length}
        color={stickyColor}
        showMenu={showMenu}
        onMenuClick={onMenuClick}
        headerExtra={headerExtra}
      />

      {/* Items container */}
      <div className="space-y-3 min-h-[200px]">
        {items.length === 0 ? (
          emptyState || <EmptyColumnState />
        ) : (
          items.map((item, index) =>
            renderItem ? (
              <div key={item.id}>{renderItem(item, index)}</div>
            ) : (
              <StickyCard
                key={item.id}
                color={stickyColor}
                title={item.title}
                subtitle={item.subtitle}
                rotate={getItemRotation(index)}
                showMenu={!!onItemMenuClick}
                onMenuClick={() => onItemMenuClick?.(item)}
                onClick={() => onItemClick?.(item)}
                size="sm"
                badges={item.badges?.map((b) => ({
                  label: b.label,
                  color: b.color as "default" | "gray" | "brown" | "orange" | "yellow" | "green" | "blue" | "purple" | "pink" | "red",
                }))}
                className="cursor-pointer"
              />
            )
          )
        )}
      </div>

      {/* Add item button */}
      {onAddItem && <AddItemButton onClick={onAddItem} />}
    </div>
  );
}

// ============================================
// Board Container Component
// ============================================

interface BoardContainerProps {
  children: ReactNode;
  className?: string;
}

export function BoardContainer({ children, className }: BoardContainerProps) {
  return (
    <div className={clsx("h-full overflow-x-auto scrollbar-thin pb-4", className)}>
      <div
        className="flex gap-6 min-h-[500px] px-4 pt-4"
        style={{ minWidth: "max-content" }}
      >
        {children}
      </div>
    </div>
  );
}

export type { BoardColumnProps, BoardColumnItem };
